import React from "react";
import {
  Alert,
  Box,
  Button,
  CopyToClipboard,
  Header,
  KeyValuePairs,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Steps,
} from "@amzn/awsui-components-react";

import {
  MIDWAY_AUDIENCE_NOTICE,
  PAYLOAD_FAMILY,
  PAYLOAD_FAMILY_LABEL,
  SOLUTION_STATUS,
  getStagesForFamily,
} from "./constants";

/** Stage state → how the step renders. */
const STAGE_STATUS = {
  done: "success",
  active: "loading",
  failed: "error",
  pending: "pending",
};

export const toSteps = (family, stages = []) => {
  const labels = getStagesForFamily(family);
  return labels.map(({ key, label }) => {
    const stage = stages.find((candidate) => candidate.key === key);
    const state = stage ? stage.state : "pending";
    return {
      status: STAGE_STATUS[state] || "pending",
      header: label,
      statusIconAriaLabel: state,
      details: stage && stage.detail ? <Box variant="small">{stage.detail}</Box> : undefined,
    };
  });
};

/**
 * Progress for one submission.
 *
 * Serves three states from one panel — in flight, held, and live — because
 * they are the same journey and a user checking back does not know which they
 * are in.
 *
 * A script sits in code review for days, so the stage detail says so. "Waiting
 * on BI — typically a few days" is the difference between a user who waits and
 * one who uploads the same thing three more times.
 */
const StatusPanel = ({ visible, onDismiss, solution, submission, onUploadFix }) => {
  if (!solution && !submission) return null;

  const family = (submission && submission.family) || (solution && solution.family);
  const id = (submission && submission.id) || (solution && solution.id);
  const name = (submission && submission.name) || (solution && solution.name);
  const stages = (submission && submission.stages) || [];
  const findings = (submission && submission.findings) || [];
  const held = findings.length > 0;
  const live = Boolean(solution) && solution.pipelineState === SOLUTION_STATUS.LIVE;

  const headerText = held ? `Held — id ${id}` : live ? `${name} is live` : `Submitted — id ${id}`;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="medium"
      header={<Header variant="h2">{headerText}</Header>}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            {held && onUploadFix && (
              <Button variant="primary" onClick={onUploadFix}>
                Upload a fix
              </Button>
            )}
            <Button onClick={onDismiss}>Done</Button>
          </SpaceBetween>
        </Box>
      }
      data-testid="byoc-status-panel"
    >
      <SpaceBetween size="l">
        {live ? (
          <>
            <KeyValuePairs
              columns={1}
              items={[
                {
                  label: "Link",
                  value: (
                    <SpaceBetween direction="horizontal" size="xs">
                      <span>{solution.link}</span>
                      <CopyToClipboard
                        copyButtonAriaLabel="Copy link"
                        copyErrorText="Failed to copy"
                        copySuccessText="Link copied"
                        textToCopy={solution.link || ""}
                        variant="icon"
                      />
                    </SpaceBetween>
                  ),
                },
                { label: "Id", value: id },
                {
                  label: "Type",
                  value:
                    family === PAYLOAD_FAMILY.SCRIPT
                      ? "Script, runnable in Accounting Central"
                      : `${PAYLOAD_FAMILY_LABEL[family]}, served from the CDN`,
                },
              ]}
            />
            {family !== PAYLOAD_FAMILY.SCRIPT && (
              <Alert type="info">{MIDWAY_AUDIENCE_NOTICE}</Alert>
            )}
            <Box variant="small">We&apos;ve emailed this to you.</Box>
          </>
        ) : (
          <>
            <Steps steps={toSteps(family, stages)} data-testid="byoc-status-steps" />

            {held ? (
              <Alert type="error" header={`${findings.length} finding${findings.length === 1 ? "" : "s"}`}>
                <SpaceBetween size="xxs">
                  {findings.map((finding, index) => (
                    <div key={`${finding.detail}-${index}`}>
                      · {finding.detail}
                      {finding.line ? `, line ${finding.line}` : ""}
                    </div>
                  ))}
                  <Box variant="small">
                    Nothing has been deployed. Fix these and upload again against id {id}.
                  </Box>
                </SpaceBetween>
              </Alert>
            ) : (
              <Box variant="small">
                <StatusIndicator type="info">
                  You can close this. We&apos;ll email you when it&apos;s ready.
                </StatusIndicator>
              </Box>
            )}
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default StatusPanel;
